export const formatDate = (date: Date) => {
    return date.toLocaleDateString()
}

export const formatDateWithDayOfWeek = (date: Date) => {
    return date.toLocaleDateString('en-GB', {
        weekday: 'short', // 'Wed'
        day: '2-digit', // '11'
        month: 'short', // 'Jun'
        year: 'numeric', // '2024'
    })
}

export const formatDateWithDayOfWeekForString = (dateString?: string) => {
    if (!dateString) return ''
    return formatDateWithDayOfWeek(new Date(dateString))
}

const secondsPerMinute = 60
const secondsPerHour = 3600

export const formatSecondsToTime = (seconds: number) => {
    let secondsRemaining = seconds

    const hours = Math.floor(secondsRemaining / secondsPerHour)
    secondsRemaining = secondsRemaining % secondsPerHour
    const minutes = Math.floor(secondsRemaining / secondsPerMinute)
    secondsRemaining = secondsRemaining % secondsPerMinute

    if (hours > 0) return `${hours}:${minutes}:${secondsRemaining}`

    if (secondsRemaining > 9) return `${minutes}:${secondsRemaining}`

    return `${minutes}:0${secondsRemaining}`
}

/**
 * True when `date` is a Date holding a parseable time (not Invalid Date).
 * `new Date('nonsense')` and `new Date(NaN)` both have NaN `getTime()`.
 */
export const isValidDate = (date: Date): boolean =>
    date instanceof Date && !Number.isNaN(date.getTime())

/**
 * Whether two dates fall on the same calendar day.
 *
 * ACA-177: an Invalid Date is never "the same day" as anything, INCLUDING
 * itself — so `isSameDay` is intentionally NOT reflexive for invalid input.
 * This keeps callers (e.g. Consult.getDateTitle's Today/Yesterday grouping)
 * from treating an unparseable startDate as a real day; they must guard with
 * `isValidDate` and render a fallback rather than "Invalid Date".
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
    if (!isValidDate(date1) || !isValidDate(date2)) {
        return false
    }
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    )
}

/**
 * The calendar day before `today`.
 *
 * ACA-177: for an Invalid Date input the "86400000 ms before" invariant cannot
 * hold (arithmetic on NaN stays NaN), so this returns an Invalid Date rather
 * than inventing a day. Callers group via isSameDay, which already rejects
 * invalid dates, so an invalid result simply never matches.
 */
export const buildYesterday = (today: Date) => {
    if (!isValidDate(today)) {
        return new Date(NaN)
    }
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    return yesterday
}

export const mapDateToTime = (date: Date) => {
    // Extract hours and minutes
    let hours = date.getHours()
    let minutes = date.getMinutes().toString()

    // Determine AM or PM
    const amPm = hours >= 12 ? 'PM' : 'AM'

    // Convert hours to 12-hour format
    hours = hours % 12
    // The hour '0' should be '12'
    hours = hours ? hours : 12

    // Pad minutes with leading zero if needed
    minutes = minutes.padStart(2, '0')

    // Combine hours, minutes, and AM/PM indicator
    return `${hours}:${minutes} ${amPm}`
}
